type UseLazyQuery<_T> = (args: any) => [any, any]

import { isValidArray } from 'mmfintech-commons'
import { useEffect, useState } from 'react'

interface IListQueryResponse<T = any> {
  content: T[]
  totalPages: number
  number: number
  size: number
  totalElements: number
}

export const useInfiniteScroll = (useGetDataListQuery: UseLazyQuery<any>, { ...queryParameters }) => {
  const [localPage, setLocalPage] = useState(0)
  const [localSize, setLocalSize] = useState(null)
  const [combinedData, setCombinedData] = useState([])
  const [shouldScroll, setShouldScroll] = useState<boolean | null>(null)

  const [getNext, { data: queryResponse, isFetching, isLoading, error, fulfilledTimeStamp }] = useGetDataListQuery({
    ...queryParameters
  })
  const {
    content: fetchData,
    number: remotePage,
    totalPages,
    totalElements
  } = (queryResponse as IListQueryResponse) || {}

  useEffect(() => {
    if (isValidArray(fetchData)) {
      if (remotePage === 0) {
        setCombinedData(fetchData)
      } else if (fulfilledTimeStamp) {
        setCombinedData(previousData => [...previousData, ...fetchData])
      }
    } else {
      setCombinedData([])
    }
  }, [fulfilledTimeStamp])

  const readMore = async (params: any = { size: 5, filter: {}, page: null }) => {
    let selectedPage = localPage + 1
    if (!isValidArray(combinedData)) {
      selectedPage = 0
    }
    if (params.page || params.page === 0) {
      selectedPage = params.page
    }
    if (totalPages && selectedPage >= totalPages) return
    setLocalPage(selectedPage)
    setLocalSize(params.size)
    try {
      await getNext({
        page: selectedPage,
        size: params.size || localSize,
        ...params.filter,
        ...queryParameters.filter
      }).unwrap()
    } catch (error) {
      console.error(error?.message)
    }
  }

  const scrollMore = () => {
    setShouldScroll(val => !val)
  }

  const resetData = () => {
    setLocalPage(0)
    setCombinedData([])
    void readMore({ ...queryParameters, page: 0 })
  }

  useEffect(() => {
    if (combinedData.length >= totalElements || shouldScroll == null) return
    void readMore({ ...queryParameters })
  }, [shouldScroll])

  return {
    combinedData,
    localPage,
    readMore,
    isLoading: isLoading,
    isFetching: isFetching,
    error,
    totalElements,
    totalPages,
    currentElementsNum: fetchData?.length * (remotePage + 1),
    scrollMore,
    hasMore: totalElements === undefined || combinedData.length < totalElements,
    hasFetched: !!fulfilledTimeStamp || !!error,
    setLocalPage,
    resetData
  }
}
